// Komponente für die erweiterten Datenfelder des "Mittlere Höhe" Inspektionsrappports
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../context/AuthContext';
import { 
  Building2, 
  Users, 
  FileText, 
  Shield, 
  AlertTriangle,
  Calendar,
  Loader2,
  Save,
  ChevronDown,
  ChevronUp,
  Fuel,
  Car,
  Bell,
  Droplet,
  Camera,
  Trash2,
  ImageIcon
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

// Standard-Werte für die Extras
const DEFAULT_EXTRAS = {
  sicherheitsbeauftragter: {
    name: '',
    adresse: '',
    plz_ort: '',
    mobile: '',
    email: ''
  },
  objekt_daten: {
    objekt: '',
    dossier_nr: '',
    baujahr: '',
    plan_parzelle: '',
    eigentuemer: '',
    eigentuemer_email: '',  // E-Mail für Versand an Eigentümer
    gemeinde: '',  // Name der Gemeinde
    gemeinde_email: '',  // E-Mail der Gemeinde für Versand
    abwart_verantwortlicher: '',
    telefon: '',
    anwesende_personen: ''
  },
  inspection_reason: {
    periodisch: true,
    nach_instandstellung: false
  },
  existing_documents: {
    brandschutzkonzept: null,
    letzter_bericht_datum: ''
  },
  building_definition: {
    gebaeudeabstand: null,
    feuerwehrzufahrt: 'ja',
    hydranten: null,
    bauart: 'RF1',
    geschosse_ug: 0,
    geschosse_eg: 1,
    geschosse_og: 0,
    geschosse_dg: 0,
    gaestebetten_ug: 0,
    gaestebetten_eg: 0,
    gaestebetten_og: 0,
    gaestebetten_dg: 0,
    angestelltenbetten_ug: 0,
    angestelltenbetten_eg: 0,
    angestelltenbetten_og: 0,
    angestelltenbetten_dg: 0
  },
  // 2.1.5 Brennstofflagerung
  brennstofflagerung: {
    heizoel_liter: 0,
    feste_brennstoffe_m3: 0,
    photos: []
  },
  // 2.1.6 Garage/Parking
  garage_parking: {
    flaeche_m2: 0,
    photos: []
  },
  // 2.2.2 Brandmeldeanlage
  brandmeldeanlage: {
    firma: '',
    photos: []
  },
  // 2.2.3 Sprinkleranlage
  sprinkleranlage: {
    firma: '',
    photos: []
  },
  schutzziel: {
    gefaehrdungsbild: ''
  },
  vormeinung: {
    positiv: true,
    negativ: false
  },
  verteiler: {
    eigentuemer: true,
    eigentuemer_email: '',
    gemeinde: true,
    gemeinde_email: '',
    kanton: true,
    kanton_email: '',
    weitere: []
  },
  naechste_inspektion: '',
  massnahmen: []
};

export function MittlereHoeheForm({ inspection, extras, onSave, disabled = false }) {
  const [formData, setFormData] = useState(DEFAULT_EXTRAS);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { user } = useAuth();

  // Laden der vorhandenen Daten
  useEffect(() => {
    if (extras) {
      setFormData(prev => ({
        ...DEFAULT_EXTRAS,
        ...extras,
        sicherheitsbeauftragter: { ...DEFAULT_EXTRAS.sicherheitsbeauftragter, ...extras.sicherheitsbeauftragter },
        objekt_daten: { ...DEFAULT_EXTRAS.objekt_daten, ...extras.objekt_daten },
        inspection_reason: { ...DEFAULT_EXTRAS.inspection_reason, ...extras.inspection_reason },
        existing_documents: { ...DEFAULT_EXTRAS.existing_documents, ...extras.existing_documents },
        building_definition: { ...DEFAULT_EXTRAS.building_definition, ...extras.building_definition },
        brennstofflagerung: { ...DEFAULT_EXTRAS.brennstofflagerung, ...extras.brennstofflagerung },
        garage_parking: { ...DEFAULT_EXTRAS.garage_parking, ...extras.garage_parking },
        brandmeldeanlage: { ...DEFAULT_EXTRAS.brandmeldeanlage, ...extras.brandmeldeanlage },
        sprinkleranlage: { ...DEFAULT_EXTRAS.sprinkleranlage, ...extras.sprinkleranlage },
        schutzziel: { ...DEFAULT_EXTRAS.schutzziel, ...extras.schutzziel },
        vormeinung: { ...DEFAULT_EXTRAS.vormeinung, ...extras.vormeinung },
        verteiler: { ...DEFAULT_EXTRAS.verteiler, ...extras.verteiler },
      }));
    }
  }, [extras]);

  // Automatisch E-Mail des eingeloggten Benutzers für Sicherheitsbeauftragter vorausfüllen
  useEffect(() => {
    if (user && !extras?.sicherheitsbeauftragter?.email) {
      setFormData(prev => {
        // Nur vorausfüllen, wenn noch keine E-Mail eingetragen ist
        if (!prev.sicherheitsbeauftragter.email) {
          return {
            ...prev,
            sicherheitsbeauftragter: {
              ...prev.sicherheitsbeauftragter,
              name: prev.sicherheitsbeauftragter.name || user.name || '',
              email: user.email || ''
            }
          };
        }
        return prev;
      });
    }
  }, [user, extras]);

  // Aus Inspektion übernehmen
  useEffect(() => {
    if (inspection && !extras) {
      setFormData(prev => ({
        ...prev,
        objekt_daten: {
          ...prev.objekt_daten,
          objekt: inspection.property_name || '',
          eigentuemer: inspection.client_name || '',
          telefon: inspection.client_contact || ''
        }
      }));
    }
  }, [inspection, extras]);

  // Automatisch E-Mails im Verteiler aktualisieren (außer KAF)
  // Eigentümer E-Mail aus Objektdaten → Verteiler
  useEffect(() => {
    const eigentuemerEmail = formData.objekt_daten?.eigentuemer_email || '';
    const currentVerteilerEmail = formData.verteiler?.eigentuemer_email || '';
    
    // Nur aktualisieren wenn Objektdaten-E-Mail vorhanden und Verteiler-E-Mail anders ist
    if (eigentuemerEmail && eigentuemerEmail !== currentVerteilerEmail) {
      setFormData(prev => ({
        ...prev,
        verteiler: {
          ...prev.verteiler,
          eigentuemer_email: eigentuemerEmail
        }
      }));
    }
  }, [formData.objekt_daten?.eigentuemer_email]);

  // Gemeinde E-Mail aus Objektdaten → Verteiler
  useEffect(() => {
    const gemeindeEmail = formData.objekt_daten?.gemeinde_email || '';
    const currentVerteilerGemeindeEmail = formData.verteiler?.gemeinde_email || '';
    
    // Nur aktualisieren wenn Objektdaten-Gemeinde-E-Mail vorhanden und anders ist
    if (gemeindeEmail && gemeindeEmail !== currentVerteilerGemeindeEmail) {
      setFormData(prev => ({
        ...prev,
        verteiler: {
          ...prev.verteiler,
          gemeinde_email: gemeindeEmail
        }
      }));
    }
  }, [formData.objekt_daten?.gemeinde_email]);

  const updateField = (section, field, value) => {
    setIsDirty(true);
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateRootField = (field, value) => {
    setIsDirty(true);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Foto-Upload-Handler für spezielle Sektionen
  const handleSectionPhotoUpload = async (section, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          photos: [...(prev[section]?.photos || []), base64]
        }
      }));
      setIsDirty(true);
    };
    reader.readAsDataURL(file);
    // Reset input
    e.target.value = '';
  };

  // Foto-Lösch-Handler für spezielle Sektionen
  const handleSectionPhotoDelete = (section, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        photos: prev[section]?.photos?.filter((_, i) => i !== index) || []
      }
    }));
    setIsDirty(true);
  };

  // Foto-Upload-Komponente für spezielle Sektionen
  const SectionPhotoUpload = ({ section, label }) => {
    const photos = formData[section]?.photos || [];
    const inputId = `photo-upload-${section}`;
    
    return (
      <div className="mt-4 pt-4 border-t border-dashed">
        <Label className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
          <Camera className="h-4 w-4" />
          Fotos ({photos.length})
        </Label>
        
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {photos.map((photo, index) => (
              <div key={index} className="relative group">
                <img 
                  src={photo} 
                  alt={`${label} Foto ${index + 1}`}
                  className="h-20 w-20 object-cover rounded-lg border"
                />
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleSectionPhotoDelete(section, index)}
                    className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        
        {!disabled && (
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleSectionPhotoUpload(section, e)}
              className="hidden"
              id={inputId}
            />
            <label
              htmlFor={inputId}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg cursor-pointer hover:bg-muted transition-colors"
            >
              <Camera className="h-4 w-4" />
              Foto hinzufügen
            </label>
          </div>
        )}
      </div>
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  // Berechnungen
  const totalGeschosse = 
    (formData.building_definition.geschosse_ug || 0) +
    (formData.building_definition.geschosse_eg || 0) +
    (formData.building_definition.geschosse_og || 0) +
    (formData.building_definition.geschosse_dg || 0);
  
  const totalGaestebetten = 
    (formData.building_definition.gaestebetten_ug || 0) +
    (formData.building_definition.gaestebetten_eg || 0) +
    (formData.building_definition.gaestebetten_og || 0) +
    (formData.building_definition.gaestebetten_dg || 0);
  
  const totalAngestelltenbetten = 
    (formData.building_definition.angestelltenbetten_ug || 0) +
    (formData.building_definition.angestelltenbetten_eg || 0) +
    (formData.building_definition.angestelltenbetten_og || 0) +
    (formData.building_definition.angestelltenbetten_dg || 0);

  return (
    <Card className="border-border/50 mt-6" data-testid="mittlere-hoehe-form">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Erweiterte Daten (Mittlere Höhe)
          </div>
          {isDirty && !disabled && (
            <Button 
              size="sm" 
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
              data-testid="save-extras-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Speichern
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" defaultValue={["sicherheit", "objekt", "definition", "grund", "dokumente", "brennstoff", "abschluss"]} className="space-y-4">
          
          {/* Sicherheitsbeauftragter */}
          <AccordionItem value="sicherheit" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                <span>Sicherheitsbeauftragter / Prüfer</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>📧 E-Mail-Versand:</strong> Die hier eingetragene E-Mail-Adresse erhält automatisch Erinnerungen bei ablaufenden Maßnahmen-Fristen.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input 
                    value={formData.sicherheitsbeauftragter.name}
                    onChange={(e) => updateField('sicherheitsbeauftragter', 'name', e.target.value)}
                    disabled={disabled}
                    placeholder="Max Brandschutz"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    E-Mail
                    <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full">
                      📧 Für Erinnerungen
                    </span>
                  </Label>
                  <Input 
                    type="email"
                    value={formData.sicherheitsbeauftragter.email}
                    onChange={(e) => updateField('sicherheitsbeauftragter', 'email', e.target.value)}
                    disabled={disabled}
                    placeholder="max@brandschutz.ch"
                    className="border-orange-300 focus:border-orange-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input 
                    value={formData.sicherheitsbeauftragter.adresse}
                    onChange={(e) => updateField('sicherheitsbeauftragter', 'adresse', e.target.value)}
                    disabled={disabled}
                    placeholder="Sicherheitsweg 5"
                  />
                </div>
                <div className="space-y-2">
                  <Label>PLZ / Ort</Label>
                  <Input 
                    value={formData.sicherheitsbeauftragter.plz_ort}
                    onChange={(e) => updateField('sicherheitsbeauftragter', 'plz_ort', e.target.value)}
                    disabled={disabled}
                    placeholder="3900 Brig"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mobile</Label>
                  <Input 
                    value={formData.sicherheitsbeauftragter.mobile}
                    onChange={(e) => updateField('sicherheitsbeauftragter', 'mobile', e.target.value)}
                    disabled={disabled}
                    placeholder="079 123 45 67"
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Objektdaten */}
          <AccordionItem value="objekt" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                <span>Objektdaten</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Objekt</Label>
                  <Input 
                    value={formData.objekt_daten.objekt}
                    onChange={(e) => updateField('objekt_daten', 'objekt', e.target.value)}
                    disabled={disabled}
                    placeholder="Hotel Matterhorn"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dossier N°</Label>
                  <Input 
                    value={formData.objekt_daten.dossier_nr}
                    onChange={(e) => updateField('objekt_daten', 'dossier_nr', e.target.value)}
                    disabled={disabled}
                    placeholder="D-2025-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Baujahr</Label>
                  <Input 
                    value={formData.objekt_daten.baujahr}
                    onChange={(e) => updateField('objekt_daten', 'baujahr', e.target.value)}
                    disabled={disabled}
                    placeholder="1985"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Plan / Parzelle</Label>
                  <Input 
                    value={formData.objekt_daten.plan_parzelle}
                    onChange={(e) => updateField('objekt_daten', 'plan_parzelle', e.target.value)}
                    disabled={disabled}
                    placeholder="Parz. 1234"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Eigentümer</Label>
                  <Input 
                    value={formData.objekt_daten.eigentuemer}
                    onChange={(e) => updateField('objekt_daten', 'eigentuemer', e.target.value)}
                    disabled={disabled}
                    placeholder="Muster Immobilien AG"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    E-Mail Eigentümer
                    <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full">
                      📧 Für Erinnerungen
                    </span>
                  </Label>
                  <Input 
                    type="email"
                    value={formData.objekt_daten.eigentuemer_email || ''}
                    onChange={(e) => updateField('objekt_daten', 'eigentuemer_email', e.target.value)}
                    disabled={disabled}
                    placeholder="eigentuemer@beispiel.ch"
                    className="border-orange-300 focus:border-orange-500"
                  />
                  <p className="text-xs text-muted-foreground">
                    📧 Erhält Prüfberichte und Maßnahmen-Erinnerungen
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Gemeinde</Label>
                  <Input 
                    value={formData.objekt_daten.gemeinde || ''}
                    onChange={(e) => updateField('objekt_daten', 'gemeinde', e.target.value)}
                    disabled={disabled}
                    placeholder="Leukerbad"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    E-Mail Gemeinde
                    <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full">
                      📧 Für Erinnerungen
                    </span>
                  </Label>
                  <Input 
                    type="email"
                    value={formData.objekt_daten.gemeinde_email || ''}
                    onChange={(e) => updateField('objekt_daten', 'gemeinde_email', e.target.value)}
                    disabled={disabled}
                    placeholder="gemeinde@leukerbad.ch"
                    className="border-orange-300 focus:border-orange-500"
                  />
                  <p className="text-xs text-muted-foreground">
                    📧 Erhält Prüfberichte und Maßnahmen-Erinnerungen
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Abwart / Verantwortlicher</Label>
                  <Input 
                    value={formData.objekt_daten.abwart_verantwortlicher}
                    onChange={(e) => updateField('objekt_daten', 'abwart_verantwortlicher', e.target.value)}
                    disabled={disabled}
                    placeholder="Peter Huber"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input 
                    value={formData.objekt_daten.telefon}
                    onChange={(e) => updateField('objekt_daten', 'telefon', e.target.value)}
                    disabled={disabled}
                    placeholder="027 123 45 67"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Anwesende Personen</Label>
                  <Input 
                    value={formData.objekt_daten.anwesende_personen}
                    onChange={(e) => updateField('objekt_daten', 'anwesende_personen', e.target.value)}
                    disabled={disabled}
                    placeholder="Max Muster (Eigentümer), Hans Prüfer (Inspektor)"
                  />
                </div>
              </div>

              {/* Grund der Inspektion */}
              <div className="pt-4 border-t">
                <Label className="mb-3 block">Grund der Inspektion</Label>
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={formData.inspection_reason.periodisch}
                      onCheckedChange={(checked) => updateField('inspection_reason', 'periodisch', checked)}
                      disabled={disabled}
                    />
                    <span className="text-sm">Periodische Kontrolle</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={formData.inspection_reason.nach_instandstellung}
                      onCheckedChange={(checked) => updateField('inspection_reason', 'nach_instandstellung', checked)}
                      disabled={disabled}
                    />
                    <span className="text-sm">Nach Instandstellung</span>
                  </div>
                </div>
              </div>

              {/* Bestehende Unterlagen */}
              <div className="pt-4 border-t">
                <Label className="mb-3 block">Bestehende Unterlagen</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm mr-4">Brandschutzkonzept:</span>
                    <RadioGroup 
                      value={formData.existing_documents.brandschutzkonzept === true ? 'ja' : formData.existing_documents.brandschutzkonzept === false ? 'nein' : undefined}
                      onValueChange={(v) => updateField('existing_documents', 'brandschutzkonzept', v === 'ja')}
                      className="flex gap-4 mt-2"
                      disabled={disabled}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="ja" id="bsk-ja" />
                        <Label htmlFor="bsk-ja" className="text-sm">Ja</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="nein" id="bsk-nein" />
                        <Label htmlFor="bsk-nein" className="text-sm">Nein</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Letzter Inspektionsbericht vom:</Label>
                    <Input 
                      type="date"
                      value={formData.existing_documents.letzter_bericht_datum}
                      onChange={(e) => updateField('existing_documents', 'letzter_bericht_datum', e.target.value)}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Definition der Baute */}
          <AccordionItem value="definition" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                <span>1.0 Definition der Baute</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-6">
              {/* 1.1 - 1.4 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm">1.1 Gebäudeabstand gem. Brandschutzvorschriften:</span>
                  <RadioGroup 
                    value={formData.building_definition.gebaeudeabstand === true ? 'ja' : formData.building_definition.gebaeudeabstand === false ? 'nein' : undefined}
                    onValueChange={(v) => updateField('building_definition', 'gebaeudeabstand', v === 'ja')}
                    className="flex gap-4 mt-2"
                    disabled={disabled}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="ja" id="ga-ja" />
                      <Label htmlFor="ga-ja" className="text-sm">Ja</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="nein" id="ga-nein" />
                      <Label htmlFor="ga-nein" className="text-sm">Nein</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <span className="text-sm">1.2 Feuerwehrzufahrt:</span>
                  <RadioGroup 
                    value={formData.building_definition.feuerwehrzufahrt}
                    onValueChange={(v) => updateField('building_definition', 'feuerwehrzufahrt', v)}
                    className="flex gap-4 mt-2"
                    disabled={disabled}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="ja" id="fwz-ja" />
                      <Label htmlFor="fwz-ja" className="text-sm">Ja</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="nein" id="fwz-nein" />
                      <Label htmlFor="fwz-nein" className="text-sm">Nein</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="abgelegen" id="fwz-abg" />
                      <Label htmlFor="fwz-abg" className="text-sm">Abgelegen</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <span className="text-sm">1.3 Hydranten (in 100m / mind. 7 bar):</span>
                  <RadioGroup 
                    value={formData.building_definition.hydranten === true ? 'ja' : formData.building_definition.hydranten === false ? 'nein' : undefined}
                    onValueChange={(v) => updateField('building_definition', 'hydranten', v === 'ja')}
                    className="flex gap-4 mt-2"
                    disabled={disabled}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="ja" id="hy-ja" />
                      <Label htmlFor="hy-ja" className="text-sm">Ja</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="nein" id="hy-nein" />
                      <Label htmlFor="hy-nein" className="text-sm">Nein</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <span className="text-sm">1.4 Bauart:</span>
                  <RadioGroup 
                    value={formData.building_definition.bauart}
                    onValueChange={(v) => updateField('building_definition', 'bauart', v)}
                    className="flex gap-4 mt-2"
                    disabled={disabled}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="RF1" id="ba-rf1" />
                      <Label htmlFor="ba-rf1" className="text-sm">RF1</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="RF2" id="ba-rf2" />
                      <Label htmlFor="ba-rf2" className="text-sm">RF2</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="RF3" id="ba-rf3" />
                      <Label htmlFor="ba-rf3" className="text-sm">RF3</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* 1.5 Geschosszahl */}
              <div className="pt-4 border-t">
                <Label className="mb-3 block font-medium">1.5 Geschosszahl</Label>
                <div className="grid grid-cols-5 gap-2 text-center text-sm">
                  <div className="font-medium">UG</div>
                  <div className="font-medium">EG</div>
                  <div className="font-medium">OG</div>
                  <div className="font-medium">DG</div>
                  <div className="font-medium">Total</div>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.geschosse_ug}
                    onChange={(e) => updateField('building_definition', 'geschosse_ug', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.geschosse_eg}
                    onChange={(e) => updateField('building_definition', 'geschosse_eg', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.geschosse_og}
                    onChange={(e) => updateField('building_definition', 'geschosse_og', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.geschosse_dg}
                    onChange={(e) => updateField('building_definition', 'geschosse_dg', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <div className="flex items-center justify-center font-bold bg-secondary/50 rounded">
                    {totalGeschosse}
                  </div>
                </div>
              </div>

              {/* 1.6 Gästebetten */}
              <div className="pt-4 border-t">
                <Label className="mb-3 block font-medium">1.6 Gästebetten</Label>
                <div className="grid grid-cols-5 gap-2 text-center text-sm">
                  <div className="font-medium">UG</div>
                  <div className="font-medium">EG</div>
                  <div className="font-medium">OG</div>
                  <div className="font-medium">DG</div>
                  <div className="font-medium">Total</div>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.gaestebetten_ug}
                    onChange={(e) => updateField('building_definition', 'gaestebetten_ug', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.gaestebetten_eg}
                    onChange={(e) => updateField('building_definition', 'gaestebetten_eg', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.gaestebetten_og}
                    onChange={(e) => updateField('building_definition', 'gaestebetten_og', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.gaestebetten_dg}
                    onChange={(e) => updateField('building_definition', 'gaestebetten_dg', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <div className="flex items-center justify-center font-bold bg-secondary/50 rounded">
                    {totalGaestebetten}
                  </div>
                </div>
              </div>

              {/* 1.7 Angestelltenbetten */}
              <div className="pt-4 border-t">
                <Label className="mb-3 block font-medium">1.7 Angestelltenbetten</Label>
                <div className="grid grid-cols-5 gap-2 text-center text-sm">
                  <div className="font-medium">UG</div>
                  <div className="font-medium">EG</div>
                  <div className="font-medium">OG</div>
                  <div className="font-medium">DG</div>
                  <div className="font-medium">Total</div>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.angestelltenbetten_ug}
                    onChange={(e) => updateField('building_definition', 'angestelltenbetten_ug', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.angestelltenbetten_eg}
                    onChange={(e) => updateField('building_definition', 'angestelltenbetten_eg', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.angestelltenbetten_og}
                    onChange={(e) => updateField('building_definition', 'angestelltenbetten_og', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <Input 
                    type="number"
                    min="0"
                    value={formData.building_definition.angestelltenbetten_dg}
                    onChange={(e) => updateField('building_definition', 'angestelltenbetten_dg', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    className="text-center"
                  />
                  <div className="flex items-center justify-center font-bold bg-secondary/50 rounded">
                    {totalAngestelltenbetten}
                  </div>
                </div>
                <div className="mt-2 text-right">
                  <span className="font-bold">Total Betten: {totalGaestebetten + totalAngestelltenbetten}</span>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 2.1.5 Brennstofflagerung & 2.1.6 Garage/Parking */}
          <AccordionItem value="brennstoff-garage" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Fuel className="h-4 w-4 text-primary" />
                <span>2.1.5 Brennstofflagerung / 2.1.6 Garage</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-6">
              {/* 2.1.5 Brennstofflagerung */}
              <div>
                <Label className="mb-3 block font-medium">2.1.5 Brennstofflagerung</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Heizöl (Liter)</Label>
                    <Input 
                      type="number"
                      min="0"
                      value={formData.brennstofflagerung.heizoel_liter}
                      onChange={(e) => updateField('brennstofflagerung', 'heizoel_liter', parseInt(e.target.value) || 0)}
                      disabled={disabled}
                      placeholder="0"
                      data-testid="brennstoff-heizoel"
                    />
                    <p className="text-xs text-muted-foreground">Max. 4000 Liter im Heizlokal</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Feste Brennstoffe (m³)</Label>
                    <Input 
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.brennstofflagerung.feste_brennstoffe_m3}
                      onChange={(e) => updateField('brennstofflagerung', 'feste_brennstoffe_m3', parseFloat(e.target.value) || 0)}
                      disabled={disabled}
                      placeholder="0"
                      data-testid="brennstoff-fest"
                    />
                    <p className="text-xs text-muted-foreground">Max. 10 m³</p>
                  </div>
                </div>
                <SectionPhotoUpload section="brennstofflagerung" label="Brennstofflagerung" />
              </div>

              {/* 2.1.6 Garage/Parking */}
              <div className="pt-4 border-t">
                <Label className="mb-3 block font-medium">2.1.6 Garage/Parking</Label>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Fläche (m²)</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.garage_parking.flaeche_m2}
                    onChange={(e) => updateField('garage_parking', 'flaeche_m2', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    placeholder="0"
                    className="max-w-xs"
                    data-testid="garage-flaeche"
                  />
                  <p className="text-xs text-muted-foreground">Ab 600 m²: BMA erforderlich | Ab 1200 m²: Sprinkler erforderlich</p>
                </div>
                <SectionPhotoUpload section="garage_parking" label="Garage/Parking" />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 2.2.2 Brandmeldeanlage & 2.2.3 Sprinkleranlage */}
          <AccordionItem value="bma-sprinkler" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-primary" />
                <span>2.2.2 BMA / 2.2.3 Sprinkler</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-6">
              {/* 2.2.2 Brandmeldeanlage */}
              <div>
                <Label className="mb-3 block font-medium">2.2.2 Brandmeldeanlage (BMA)</Label>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Wartungsfirma</Label>
                  <Input 
                    value={formData.brandmeldeanlage.firma}
                    onChange={(e) => updateField('brandmeldeanlage', 'firma', e.target.value)}
                    disabled={disabled}
                    placeholder="z.B. Securitas AG, Siemens Schweiz"
                    data-testid="bma-firma"
                  />
                </div>
                <SectionPhotoUpload section="brandmeldeanlage" label="Brandmeldeanlage" />
              </div>

              {/* 2.2.3 Sprinkleranlage */}
              <div className="pt-4 border-t">
                <Label className="mb-3 block font-medium">2.2.3 Sprinkleranlage</Label>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Wartungsfirma</Label>
                  <Input 
                    value={formData.sprinkleranlage.firma}
                    onChange={(e) => updateField('sprinkleranlage', 'firma', e.target.value)}
                    disabled={disabled}
                    placeholder="z.B. VKF-anerkannte Sprinkler-Firma"
                    data-testid="sprinkler-firma"
                  />
                </div>
                <SectionPhotoUpload section="sprinkleranlage" label="Sprinkleranlage" />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Schutzziel */}
          <AccordionItem value="schutzziel" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                <span>3.0 Schutzziel</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              <div className="space-y-2">
                <Label>3.1 Gefährdungsbild</Label>
                <Textarea 
                  value={formData.schutzziel.gefaehrdungsbild}
                  onChange={(e) => updateField('schutzziel', 'gefaehrdungsbild', e.target.value)}
                  disabled={disabled}
                  placeholder="Beschreiben Sie das Gefährdungsbild..."
                  rows={4}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Vormeinung & Abschluss */}
          <AccordionItem value="abschluss" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                <span>Abschluss & Nächste Inspektion</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-4">
              <div className="space-y-2">
                <Label>5.0 Nächste Inspektion</Label>
                <Input 
                  type="date"
                  value={formData.naechste_inspektion}
                  onChange={(e) => updateRootField('naechste_inspektion', e.target.value)}
                  disabled={disabled}
                />
              </div>

              <div className="pt-4 border-t">
                <Label className="mb-3 block">Vormeinung</Label>
                <RadioGroup 
                  value={formData.vormeinung.positiv ? 'positiv' : 'negativ'}
                  onValueChange={(v) => {
                    updateField('vormeinung', 'positiv', v === 'positiv');
                    updateField('vormeinung', 'negativ', v === 'negativ');
                  }}
                  className="flex gap-6"
                  disabled={disabled}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="positiv" id="vm-pos" />
                    <Label htmlFor="vm-pos" className="text-sm font-medium text-green-500">POSITIV</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="negativ" id="vm-neg" />
                    <Label htmlFor="vm-neg" className="text-sm font-medium text-red-500">NEGATIV</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="pt-4 border-t">
                <Label className="mb-3 block">Verteiler (Berichtsempfänger)</Label>
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>📧 E-Mail-Versand:</strong> Die hier eingetragenen E-Mail-Adressen erhalten den Inspektionsbericht und Erinnerungen bei ablaufenden Maßnahmen-Fristen.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Eigentümer */}
                  <div className="space-y-2 p-3 border rounded-lg bg-green-50 dark:bg-green-950">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={formData.verteiler.eigentuemer}
                        onCheckedChange={(checked) => updateField('verteiler', 'eigentuemer', checked)}
                        disabled={disabled}
                      />
                      <span className="text-sm font-medium">Eigentümer</span>
                    </div>
                    {formData.verteiler.eigentuemer && (
                      <>
                        <Input 
                          type="email"
                          placeholder="eigentuemer@beispiel.ch"
                          value={formData.verteiler.eigentuemer_email || ''}
                          onChange={(e) => updateField('verteiler', 'eigentuemer_email', e.target.value)}
                          disabled={disabled}
                          className="text-sm border-green-300 focus:border-green-500"
                        />
                        {formData.objekt_daten?.eigentuemer_email && formData.verteiler.eigentuemer_email === formData.objekt_daten.eigentuemer_email && (
                          <p className="text-xs text-green-700 dark:text-green-300">
                            ✓ Automatisch aus Objektdaten übernommen
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Gemeinde */}
                  <div className="space-y-2 p-3 border rounded-lg bg-green-50 dark:bg-green-950">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={formData.verteiler.gemeinde}
                        onCheckedChange={(checked) => updateField('verteiler', 'gemeinde', checked)}
                        disabled={disabled}
                      />
                      <span className="text-sm font-medium">Gemeinde</span>
                    </div>
                    {formData.verteiler.gemeinde && (
                      <>
                        <Input 
                          type="email"
                          placeholder="gemeinde@beispiel.ch"
                          value={formData.verteiler.gemeinde_email || ''}
                          onChange={(e) => updateField('verteiler', 'gemeinde_email', e.target.value)}
                          disabled={disabled}
                          className="text-sm border-green-300 focus:border-green-500"
                        />
                        {formData.objekt_daten?.gemeinde_email && formData.verteiler.gemeinde_email === formData.objekt_daten.gemeinde_email && (
                          <p className="text-xs text-green-700 dark:text-green-300">
                            ✓ Automatisch aus Objektdaten übernommen
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Kanton */}
                  <div className="space-y-2 p-3 border rounded-lg bg-amber-50 dark:bg-amber-950">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={formData.verteiler.kanton}
                        onCheckedChange={(checked) => updateField('verteiler', 'kanton', checked)}
                        disabled={disabled}
                      />
                      <span className="text-sm font-medium">Kant. Amt für Feuerwesen</span>
                    </div>
                    {formData.verteiler.kanton && (
                      <Input 
                        type="email"
                        placeholder="kaf@vs.ch"
                        value={formData.verteiler.kanton_email || ''}
                        onChange={(e) => updateField('verteiler', 'kanton_email', e.target.value)}
                        disabled={disabled}
                        className="text-sm border-orange-300 focus:border-orange-500"
                      />
                    )}
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      📧 Für KAF-Inspektionen
                    </p>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

        </Accordion>

        {/* Save Button (bottom) */}
        {isDirty && !disabled && (
          <div className="mt-6 flex justify-end">
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
              data-testid="save-extras-btn-bottom"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Änderungen speichern
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MittlereHoeheForm;
